import React, { useState, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { X, Check, Container, Palette, Truck } from 'lucide-react';

// Predefined color palette
const COLOR_PALETTE = [
  // Row 1 - Primary colors
  '#ef4444', '#f97316', '#eab308', '#22c55e', '#14b8a6', '#3b82f6', '#8b5cf6', '#ec4899',
  // Row 2 - Lighter variants
  '#fca5a5', '#fdba74', '#fde047', '#86efac', '#5eead4', '#93c5fd', '#c4b5fd', '#f9a8d4',
  // Row 3 - Neutrals
  '#ffffff', '#d1d5db', '#9ca3af', '#6b7280', '#4b5563', '#374151', '#1f2937', '#000000',
];

// Default chassis types
const DEFAULT_CHASSIS_TYPES = [
  { id: 'own', code: 'OWN', name: 'OWN' },
  { id: 'pool', code: 'POOL', name: 'POOL' },
];

export const CellChassisColorPicker = ({ 
  currentColor, 
  currentChassisType,
  currentChassisNumber,
  onColorChange, 
  onChassisChange,
  children,
  disabled = false,
  containerNumber = null,
  chassisTypes = []
}) => {
  const [open, setOpen] = useState(false);
  const [chassisType, setChassisType] = useState('');
  const [chassisNumber, setChassisNumber] = useState('');
  const [saving, setSaving] = useState(false);

  // Initialize values when popover opens
  useEffect(() => {
    if (open) {
      setChassisType(currentChassisType || '');
      setChassisNumber(currentChassisNumber || '');
    }
  }, [currentChassisType, currentChassisNumber, open]);

  const handleColorSelect = (color) => {
    onColorChange(color);
  };

  const handleClearColor = () => {
    onColorChange(null);
  };

  // Get available chassis types
  const availableChassisTypes = chassisTypes && chassisTypes.length > 0 
    ? chassisTypes 
    : DEFAULT_CHASSIS_TYPES;

  // Handle chassis type change
  const handleChassisTypeChange = async (type) => {
    setChassisType(type);
    // Auto-save when type changes
    if (onChassisChange) {
      setSaving(true);
      try {
        await onChassisChange(type, chassisNumber);
      } catch (e) {
        console.error('Failed to save chassis type:', e);
      } finally {
        setSaving(false);
      }
    }
  };

  // Handle chassis number change (on blur for text input)
  const handleChassisNumberBlur = async () => {
    if (onChassisChange && chassisNumber !== currentChassisNumber) {
      setSaving(true);
      try {
        await onChassisChange(chassisType, chassisNumber);
      } catch (e) {
        console.error('Failed to save chassis number:', e);
      } finally {
        setSaving(false);
      }
    }
  };

  // Save all chassis info
  const handleSave = async () => {
    setSaving(true);
    try {
      await onChassisChange(chassisType, chassisNumber);
      setOpen(false);
    } catch (e) {
      console.error('Failed to save chassis info:', e);
    } finally {
      setSaving(false);
    }
  };

  // Clear chassis info
  const handleClear = async () => {
    setSaving(true);
    try {
      setChassisType('');
      setChassisNumber('');
      await onChassisChange('', '');
      setOpen(false);
    } catch (e) {
      console.error('Failed to clear chassis info:', e);
    } finally {
      setSaving(false);
    }
  };

  const handleClick = (e) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setOpen(true);
  };

  // Long press handler for mobile
  const handleTouchStart = (e) => {
    if (disabled) return;
    const timer = setTimeout(() => {
      setOpen(true);
    }, 400);
    e.target.dataset.longPressTimer = timer;
  };

  const handleTouchEnd = (e) => {
    if (e.target.dataset.longPressTimer) {
      clearTimeout(parseInt(e.target.dataset.longPressTimer));
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <div 
          onClick={handleClick}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
          className="w-full h-full min-h-[28px] flex items-center cursor-pointer"
          data-testid="cell-chassis-picker-trigger"
        >
          {children}
        </div>
      </PopoverTrigger>
      <PopoverContent 
        className="w-[320px] p-4 bg-slate-900 border-slate-700" 
        align="start"
        sideOffset={5}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="space-y-4">
          {/* Container Number Header */}
          {containerNumber && (
            <div className="flex items-center gap-2 pb-3 border-b border-slate-700">
              <Container className="h-5 w-5 text-blue-400 flex-shrink-0" />
              <span className="text-base font-mono font-semibold text-blue-400 truncate">
                {containerNumber}
              </span>
            </div>
          )}
          
          {/* Chassis Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Truck className="h-4 w-4 text-amber-400" />
              <Label className="text-sm font-medium text-white">Chassis Information</Label>
            </div>
            
            {/* Chassis Type */}
            <div className="space-y-1">
              <Label className="text-xs text-slate-400">Chassis Type</Label>
              <Select 
                value={chassisType || "none"} 
                onValueChange={(v) => handleChassisTypeChange(v === "none" ? "" : v)}
              >
                <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  <SelectItem value="none" className="text-slate-400">Select type</SelectItem>
                  {availableChassisTypes.map(ct => (
                    <SelectItem 
                      key={ct.id || ct.code} 
                      value={ct.code || ct.name} 
                      className="text-white"
                    >
                      {ct.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Chassis Number */}
            <div className="space-y-1">
              <Label className="text-xs text-slate-400">Chassis Number</Label>
              <Input
                value={chassisNumber}
                onChange={(e) => setChassisNumber(e.target.value)}
                onBlur={handleChassisNumberBlur}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSave();
                  }
                }}
                placeholder="Enter chassis #"
                className="bg-slate-800 border-slate-600 text-white"
              />
            </div>
            
            {/* Action buttons */}
            <div className="flex items-center gap-2 pt-2">
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saving}
                className="flex-1 h-9 bg-green-600 hover:bg-green-500"
              >
                {saving ? (
                  <span className="animate-spin mr-2">↻</span>
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )}
                Save
              </Button>
              {(currentChassisType || currentChassisNumber) && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleClear}
                  disabled={saving}
                  className="h-9 px-3 border-slate-600 text-slate-400 hover:text-white"
                  title="Clear chassis info"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
          
          <Separator className="bg-slate-700" />
          
          {/* Color Picker Section */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Palette className="h-4 w-4 text-pink-400" />
                <Label className="text-sm font-medium text-white">Text Color</Label>
              </div>
              {currentColor && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearColor}
                  className="text-slate-400 hover:text-white h-7 px-2"
                >
                  <X className="h-3 w-3 mr-1" />
                  Clear
                </Button>
              )}
            </div>
            
            {/* Color Grid */}
            <div className="grid grid-cols-8 gap-1.5">
              {COLOR_PALETTE.map((color) => (
                <button
                  key={color}
                  onClick={() => handleColorSelect(color)}
                  className={`w-7 h-7 rounded-md border transition-transform hover:scale-110 active:scale-95 flex items-center justify-center touch-manipulation ${
                    currentColor === color 
                      ? 'border-white ring-2 ring-blue-500' 
                      : 'border-slate-600 hover:border-slate-400'
                  }`}
                  style={{ backgroundColor: color }}
                  title={color}
                >
                  {currentColor === color && (
                    <Check className={`h-3 w-3 ${color === '#ffffff' || color === '#fde047' || color === '#86efac' ? 'text-black' : 'text-white'}`} />
                  )}
                </button>
              ))}
            </div>
          </div>
          
          <p className="text-[10px] text-slate-500 text-center pt-2 border-t border-slate-800">
            Click cell to edit • Colors save automatically
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default CellChassisColorPicker;
